<template>
    <b-container fluid>
        <div
            v-for="(day, index) in timeline"
            :key="index"
            class="d-flex align-items-start"
        >
            <div class="d-none d-sm-inline-block">
                <timeline-icon
                    :type="day.type"
                    class="ml-0 mr-3"
                />
            </div>

            <div class="mb-3 flex-grow-1">
                <div>
                    <component
                        :is="getComponent(day)"
                        :data="day"
                        :index="index"
                        headerClass="rounded"
                        @save="save"
                    />
                </div>
            </div>
        </div>

        <div class="d-flex align-items-start add-new-day">
            <timeline-icon type="" class="ml-0 mr-3"/>
            <hemocir-button
                :title="$t('surgery.addNewDay')"
                class="mr-3"
                font-awesome-icon="fa-plus-circle"
                variant="secondary"
                @click="$bvModal.show('add-postoperatory-modal')"
            />

            <hemocir-button
                :title="$t('finalevaluation.add')"
                font-awesome-icon="fa-plus-circle"
                variant="secondary"
                @click="$router.push({name: 'surgery-final-evaluation', params: {surgeryId: surgery.id}})"
            />
        </div>

        <b-modal id="add-postoperatory-modal" centered hide-footer>
            <template v-slot:modal-title>
                {{  $t('postSurgery.addPopup.title') | capitalize }}
            </template>
            <detail-surgery-day @dateselected="dateSelected" @close="closeModal"/>
        </b-modal>
    </b-container>
</template>

<script>
    import CreateEditMixin from "@/components/mixins/CreateEditMixin";
    import DetailSurgeryDay from "@/components/surgery/detail/DetailSurgeryDay";
    import {mapStateProperty} from "@/utils/VuexHelpers";
    import {extractDate, isNull} from "@/utils/formatting";
    import {mapState} from "vuex";
    import moment from "moment";
    import TimelineBasalStudy from "@/components/surgery/detail/timeline/TimelineBasalStudy";
    import TimelineSurgery from "@/components/surgery/detail/timeline/TimelineSurgery";
    import TimelineIcon from "@/components/surgery/detail/timeline/TimelineIcon";
    import TimelineDate from "@/components/surgery/detail/timeline/TimelineDate";
    import TimelineDay from "@/components/surgery/detail/timeline/TimelineDay";
    import ButtonWithIcon from "@/components/HemocirButton";

    export default {
        name: "DetailSurgery",
        components: {
            ButtonWithIcon,
            DetailSurgeryDay,
            TimelineIcon,
        },
        mixins: [CreateEditMixin],
        props: {
            patientId: {
                type: Number
            },
            createEmpty: {
                type: Boolean,
                default: true,
            }
        },
        data() {
            return {
                isLoading: true,
                isEditingSurgery: false,
                isEditingPostSurgery: false,
                isEditingFinalEvaluation: false,
                modalTitle: '',
                modalComponent: '',
                selectedElement: null,
                createElementDate: null,
                localTimeline: [],
            };
        },
        computed: {
            ...mapState([
                'surgery',
                'surgeryFields',
                'bloodTestFields',
                'transfusionFields',
                'novoeightTreatmentFields',
            ]),
            ...mapStateProperty(['surgery.post_surgery_days']),
            editable() {
                return !this.isEditingSurgery && !this.isEditingPostSurgery && !this.isEditingFinalEvaluation;
            },
            timeline() {
                let elements = [
                    ...this.surgery.post_surgery_days
                        .filter(x => x.date !== extractDate(this.surgery.date))
                        .map(x => { return {...x, 'type': 'elements'}; }),
                    {
                        'type': 'basalstudy',
                        'date': this.surgery.basal_date,
                        'basal_inhibitor_actual': this.surgery.basal_inhibitor_actual,
                        'is_basal_sample_frozen': this.surgery.is_basal_sample_frozen,
                        ...this.surgery.basal_blood_test,
                    },
                    {
                        'type': 'surgery',
                        'date': this.surgery.date,
                    },
                ];

                if (!isNull(this.surgery.hospitalization_date)) {
                    elements.push({
                        'type': 'hospitalization',
                        'date': this.surgery.hospitalization_date,
                        'title':  this.$t('surgery.hospitalization'),
                    });
                }

                if (this.surgery.final_evaluation.discharge_patient_date !== null) {
                    elements.push({
                        'type': 'discharge',
                        'title': this.$t('surgery.dischardDate'),
                        'date': this.surgery.final_evaluation.discharge_patient_date,
                    });
                }

                let self = this;
                this.surgery.final_evaluation.readmissions.forEach(function (readmission) {
                    elements.push({
                        'type': 'readmission',
                        'title': self.$t('finalevaluation.readmission'),
                        'date': readmission.date,
                    });
                });

                elements = elements.concat(this.localTimeline);

                return elements.sort((a, b) => {
                    let firstDate = moment(a.date, moment.ISO_8601, true);
                    let secondDate = moment(b.date, moment.ISO_8601, true);
                    if (firstDate.isSame(secondDate)) {
                        if (a.type === 'surgery') { return -1; }
                        if (b.type === 'surgery') { return 1; }
                        return 0;
                    }

                    return firstDate.isBefore(secondDate) ? -1 : 1;
                });
            }
        },
        methods: {
            dateSelected(date) {
                if (!isNull(date)) {
                    if (this.timeline.findIndex(element => date === element.date) === -1) {
                        this.localTimeline.push({date: date, type: 'elements', data: []});
                    }
                }
                this.$bvModal.hide('add-postoperatory-modal');
            },
            addElement(date) {
                this.createElementDate = moment(date, moment.ISO_8601, true).format();
                this.$bvModal.show('create-element-modal');
            },
            save() {
                this.localTimeline = [];
                this.$emit('save');
                this.closeModal();
            },
            closeModal() {
                this.$bvModal.hide('add-postoperatory-modal');
            },
            getComponent(day) {
                if (day.type === 'basalstudy') {
                    return TimelineBasalStudy;
                }
                if (day.type === 'surgery') {
                    return TimelineSurgery;
                }
                if (day.type === 'elements') {
                    return TimelineDay;
                }
                return TimelineDate;
            },
        },
    };
</script>

